'use client'

import { clsx } from 'clsx'
import { type ComponentPropsWithoutRef, type ElementType, type FC } from 'react'

import {
  type CrossSellProduct,
  type Metadata,
} from '@redteclab/api/clients/retail-media-xsell-service'

import { AD_SERVER_XSELL_PRODUCT_ID_DATA_ATTR } from '../../../ad-server'
import { removeDiscountFromCrossSellProduct } from '../../../experiments/discount-experiement-fr/removeDiscountFromCrossSellProduct'
import { useGetExperiment } from '../../../experiments/hooks'
import { EXPERIMENT_NAME_DISCOUNT_REMOVAL_FR } from '../../../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../../../experiments/model/EXPERIMENT_VARIATION'
import { useCrossSellMetadataContext } from '../CrossSellMetatadaProvider'

import { CrossSellProductImage } from './CrossSellProductImage'
import { CrossSellProductName } from './CrossSellProductName'
import { CrossSellProductPrice } from './CrossSellProductPrice'
import { CrossSellProductQuantity } from './CrossSellProductQuantity'
import { CrossSellProductRating } from './CrossSellProductRating'

type CrossSellHorizontalSliderItemProps =
  ComponentPropsWithoutRef<'section'> & {
    product?: CrossSellProduct
    slots?: {
      image?: ElementType
    }
  }

export const CrossSellProductCard: FC<CrossSellHorizontalSliderItemProps> = ({
  className,
  product: productProp,
  slots,
  ...rest
}) => {
  let product = productProp
  const ImageComponent = slots?.image ?? CrossSellProductImage
  const metadata = useCrossSellMetadataContext()
  const metadataContent = product?.id
    ? metadata?.[product.id as keyof Metadata]
    : undefined

  const discountExperimentFr = useGetExperiment(
    EXPERIMENT_NAME_DISCOUNT_REMOVAL_FR,
  )

  if (
    product &&
    discountExperimentFr.isEnabled &&
    discountExperimentFr.variant === EXPERIMENT_VARIATION.V1
  ) {
    product = removeDiscountFromCrossSellProduct(product)
  }

  const adServerProps = {
    /*
     * Re-ranked / injected elements from ad server needs to be marked here
     * They're being grabbed in ProductAvailableCrossSellSliderWidget.tsx
     * and pushed into ad server data layer
     */
    [AD_SERVER_XSELL_PRODUCT_ID_DATA_ATTR]:
      metadataContent && product?.id ? product.id : undefined,
  }

  return (
    <section
      className={clsx(
        'mb-2 flex flex-1 flex-col overflow-hidden rounded-2xl bg-light-primary-low shadow-m',
        className,
      )}
      data-product-id={product?.id}
      {...adServerProps}
      {...rest}
    >
      <ImageComponent product={product} />
      <div className="flex flex-col gap-1 px-2 py-2.5">
        <CrossSellProductName product={product} />
        <div className="flex-1 justify-between">
          <CrossSellProductRating product={product} />
          <CrossSellProductQuantity product={product} />
          <CrossSellProductPrice product={product} />
        </div>
      </div>
    </section>
  )
}
